import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { hasTier } from "@/lib/tiers";
import { PaywallGate } from "@/components/PaywallGate";
import {
  Compass, FileText, Loader2, Copy, Check, AlertTriangle, ArrowLeft, Sparkles,
  Calendar, ListOrdered, HelpCircle, ClipboardList, FileSignature, Search, Target, Gavel, FolderKanban,
} from "lucide-react";

const TYPE_ICONS = {
  case_timeline: Calendar,
  issue_list: ListOrdered,
  question_list: HelpCircle,
  fact_summary: ClipboardList,
  declaration: FileSignature,
  discovery_request: Search,
  hearing_prep: Target,
  motion_outline: Gavel,
  evidence_exhibit: FolderKanban,
};

const COPY = {
  en: {
    kicker: "DIY Legal · Complete +",
    heading: "Document & Argument Builder",
    sub: "Organize what you already know into a clean draft. Every document is labeled DRAFT — REVIEW BEFORE USE. Nothing is invented for you.",
    pick_title: "Pick what to build",
    title_label: "Title (optional)",
    title_placeholder: "e.g., Timeline for Case 2024-SC-1234",
    context_label: "Case context (parties, court, what happened)",
    context_placeholder: "Example: I received a small-claims summons from ABC Collections on Feb 3 for a $2,400 alleged debt. Cook County, Illinois. My court date is April 12.",
    field_add: "Add a detail",
    field_key: "Detail (e.g., Filing date, Party names, Claim amount)",
    field_value: "What you know",
    remove: "Remove",
    submit: "Generate DRAFT",
    generating: "Building your draft...",
    back: "Start a new document",
    copy: "Copy",
    copied: "Copied",
    error_prefix: "Something went wrong:",
    recent: "Your recent drafts",
    empty_recent: "No drafts yet. Pick a document type on the left to start.",
    disclaimer: "DIY Legal is not a law firm. Every document is a DRAFT for you to review. Do not file anything with a court until you (or a licensed attorney) have reviewed it.",
  },
  es: {
    kicker: "DIY Legal · Complete +",
    heading: "Constructor de Documentos y Argumentos",
    sub: "Organiza lo que ya sabes en un borrador limpio. Cada documento se marca BORRADOR — REVISAR ANTES DE USAR. No se inventa nada por ti.",
    pick_title: "Elige qué construir",
    title_label: "Título (opcional)",
    title_placeholder: "ej., Cronología del Caso 2024-SC-1234",
    context_label: "Contexto del caso (partes, corte, qué pasó)",
    context_placeholder: "Ejemplo: Recibí una citación de small-claims de ABC Collections el 3 de febrero por una supuesta deuda de $2,400. Condado de Cook, Illinois. Mi fecha de corte es el 12 de abril.",
    field_add: "Agregar un detalle",
    field_key: "Detalle (ej., Fecha de presentación, Nombres de las partes)",
    field_value: "Lo que sabes",
    remove: "Quitar",
    submit: "Generar BORRADOR",
    generating: "Armando tu borrador...",
    back: "Empezar un nuevo documento",
    copy: "Copiar",
    copied: "Copiado",
    error_prefix: "Algo salió mal:",
    recent: "Tus borradores recientes",
    empty_recent: "Aún no hay borradores. Elige un tipo de documento a la izquierda para comenzar.",
    disclaimer: "DIY Legal no es un bufete de abogados. Cada documento es un BORRADOR para que lo revises. No presentes nada en la corte sin revisarlo tú (o un abogado con licencia).",
  },
};

function BuilderApp() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [types, setTypes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [title, setTitle] = useState("");
  const [context, setContext] = useState("");
  const [fields, setFields] = useState([{ k: "", v: "" }]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/builder/types").then((r) => setTypes(r.data.types || [])).catch(() => {});
    api.get("/builder/documents").then((r) => setRecent(r.data.documents || [])).catch(() => {});
  }, []);

  const label = (t) => t?.[lang] || t?.en;

  const submit = async () => {
    if (!selected || loading) return;
    setLoading(true);
    setErr("");
    setResult(null);
    const cleanFields = Object.fromEntries(
      fields.filter((f) => f.k.trim() && f.v.trim()).map((f) => [f.k.trim(), f.v.trim()])
    );
    try {
      const { data } = await api.post("/builder/generate", {
        kind: selected.key,
        title: title.trim() || null,
        case_context: context.trim() || null,
        fields: cleanFields,
        lang,
      });
      setResult(data);
      const list = await api.get("/builder/documents");
      setRecent(list.data.documents || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const doCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };

  const reset = () => {
    setResult(null);
    setSelected(null);
    setTitle("");
    setContext("");
    setFields([{ k: "", v: "" }]);
    setErr("");
  };

  if (result) {
    return (
      <div className="max-w-3xl mx-auto py-6 px-1" data-testid="builder-result">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <button onClick={reset} data-testid="builder-back" className="pod-focus text-sm font-extrabold uppercase tracking-widest text-[#C85A32] hover:underline inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> {c.back}
          </button>
          <button onClick={doCopy} data-testid="builder-copy" className="pod-focus text-sm font-bold border-2 border-[#E4DDD3] hover:border-[#C85A32] text-[#1C2024] px-3 py-2 rounded-xl inline-flex items-center gap-2">
            {copied ? <><Check className="w-4 h-4 text-emerald-600" /> {c.copied}</> : <><Copy className="w-4 h-4" /> {c.copy}</>}
          </button>
        </div>
        <div className="rounded-2xl bg-white border-2 border-[#E4DDD3] p-5 sm:p-8 shadow-sm">
          <div className="rounded-xl bg-amber-50 border-2 border-amber-300 text-amber-900 px-4 py-2 text-xs font-extrabold uppercase tracking-widest inline-flex items-center gap-2 mb-4" data-testid="builder-draft-badge">
            <AlertTriangle className="w-3.5 h-3.5" /> {lang === "es" ? "BORRADOR — REVISAR ANTES DE USAR" : "DRAFT — REVIEW BEFORE USE"}
          </div>
          <pre className="whitespace-pre-wrap font-sans text-[15px] text-[#1C2024] leading-relaxed" data-testid="builder-markdown">{result.markdown}</pre>
        </div>
        <p className="mt-6 text-xs text-[#707880] italic text-center max-w-xl mx-auto">{c.disclaimer}</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-6 px-1" data-testid="builder-page">
      <div className="text-center">
        <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32] font-extrabold inline-flex items-center gap-2">
          <Compass className="w-3.5 h-3.5" /> {c.kicker}
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-[#1C2024]">{c.heading}</h1>
        <p className="mt-2 text-[#4A525A] max-w-2xl mx-auto">{c.sub}</p>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        {/* Type picker */}
        <aside className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-4">
          <div className="text-xs uppercase tracking-widest font-extrabold text-[#707880] mb-2">{c.pick_title}</div>
          <ul className="space-y-1.5">
            {types.map((t) => {
              const Icon = TYPE_ICONS[t.key] || FileText;
              const active = selected?.key === t.key;
              return (
                <li key={t.key}>
                  <button
                    onClick={() => setSelected(t)}
                    data-testid={`builder-type-${t.key}`}
                    className={`pod-focus w-full flex items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition-colors ${
                      active ? "bg-[#C85A32] text-white" : "text-[#1C2024] hover:bg-[#FAF5EC]"
                    }`}
                  >
                    <Icon className="w-4 h-4 shrink-0" /> {label(t)}
                  </button>
                </li>
              );
            })}
          </ul>
          {recent.length > 0 && (
            <>
              <div className="text-xs uppercase tracking-widest font-extrabold text-[#707880] mt-6 mb-2">{c.recent}</div>
              <ul className="space-y-1 text-sm">
                {recent.slice(0, 6).map((d) => (
                  <li key={d.id} data-testid={`builder-recent-${d.id}`} className="text-[#4A525A] leading-tight truncate">
                    <span className="font-semibold text-[#1C2024]">{d.title}</span>
                    <span className="ml-1 text-[11px] text-[#707880]">· {new Date(d.created_at).toLocaleDateString(lang === "es" ? "es-US" : "en-US")}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </aside>

        {/* Form */}
        <section className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5 space-y-4">
          {!selected ? (
            <div className="text-center py-10 text-[#4A525A]" data-testid="builder-empty">{c.pick_title} →</div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-sm text-[#1C2024]">
                <Sparkles className="w-4 h-4 text-[#C85A32]" />
                <span className="font-bold">{label(selected)}</span>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">{c.title_label}</label>
                <input
                  data-testid="builder-title"
                  className="mt-2 w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:outline-none px-4 py-2.5 text-[#1C2024]"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={c.title_placeholder}
                  maxLength={200}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">{c.context_label}</label>
                <textarea
                  data-testid="builder-context"
                  className="mt-2 w-full min-h-[110px] rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:outline-none px-4 py-3 text-[#1C2024]"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder={c.context_placeholder}
                  maxLength={4000}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">{c.field_add}</label>
                <div className="mt-2 space-y-2">
                  {fields.map((f, i) => (
                    <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2" data-testid={`builder-field-${i}`}>
                      <input
                        className="rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:outline-none px-3 py-2 text-sm"
                        value={f.k}
                        onChange={(e) => setFields((x) => x.map((r, j) => j === i ? { ...r, k: e.target.value } : r))}
                        placeholder={c.field_key}
                      />
                      <input
                        className="rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:outline-none px-3 py-2 text-sm"
                        value={f.v}
                        onChange={(e) => setFields((x) => x.map((r, j) => j === i ? { ...r, v: e.target.value } : r))}
                        placeholder={c.field_value}
                      />
                      {fields.length > 1 && (
                        <button
                          onClick={() => setFields((x) => x.filter((_, j) => j !== i))}
                          data-testid={`builder-field-remove-${i}`}
                          className="pod-focus text-xs font-bold text-[#707880] hover:text-[#B24C28] px-2"
                        >{c.remove}</button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => setFields((x) => [...x, { k: "", v: "" }])}
                    data-testid="builder-field-add"
                    className="pod-focus text-xs font-extrabold uppercase tracking-widest text-[#C85A32] hover:underline"
                  >+ {c.field_add}</button>
                </div>
              </div>

              {err && <div className="text-sm text-red-800 bg-red-50 border-2 border-red-200 rounded-xl p-3" data-testid="builder-error">{c.error_prefix} {err}</div>}

              <button
                onClick={submit}
                disabled={loading || !selected}
                data-testid="builder-submit"
                className="pod-focus w-full sm:w-auto bg-[#C85A32] hover:bg-[#B24C28] disabled:opacity-70 text-white font-bold py-3 px-6 rounded-xl inline-flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                {loading ? c.generating : c.submit}
              </button>
            </>
          )}
        </section>
      </div>

      <p className="mt-6 text-xs text-[#707880] italic text-center max-w-2xl mx-auto">{c.disclaimer}</p>
    </div>
  );
}

export default function Builder() {
  const { user } = useAuth();
  const tier = user?.tier || "free";
  if (!user || !hasTier(tier, "complete")) {
    return <PaywallGate feature="builder" minimum="complete"><BuilderApp /></PaywallGate>;
  }
  return <BuilderApp />;
}
